import React from 'react';
import { ReactComponent as CDCDiagram } from './assets/cdc.svg';
import { ReactComponent as ReplicationDiagram } from './assets/replication.svg';
import { ReactComponent as StreamingDiagram } from './assets/streaming.svg';
import { ReactComponent as SparkDiagram } from './assets/spark.svg';
import { ReactComponent as InMemoryDiagram } from './assets/in-memory.svg';
import { ReactComponent as ELTDiagram } from './assets/elt.svg';
import { ReactComponent as RAGDiagram } from './assets/rag.svg';
import { ReactComponent as AutomaticDiagram } from './assets/auto.svg';
import { ReactComponent as BYODiagram } from './assets/byo.svg';
import { ReactComponent as APIServerIcon } from './assets/api-server.svg';

import { ReactComponent as CDC } from 'new/components/assets/flowTypeCDC.svg';
import { ReactComponent as ELT } from 'new/components/assets/flowTypeELT.svg';
import { ReactComponent as InMemory } from 'new/components/assets/flowTypeInMemory.svg';
import { ReactComponent as Replication } from 'new/components/assets/flowTypeReplication.svg';
import { ReactComponent as Spark } from 'new/components/assets/flowTypeSpark.svg';
import { ReactComponent as Streaming } from 'new/components/assets/flowIconStreaming.svg';
import { ReactComponent as RAG } from 'new/components/assets/flowTypeRAG.svg';
import { ReactComponent as BYO } from 'new/components/assets/flowTypeBYO.svg';
import { ReactComponent as Auto } from 'new/components/assets/flowTypeAuto.svg';
import { ReactComponent as APIServer } from 'new/components/icons/fi-rr-browser.svg';

import { FLOW_CATEGORY, FLOW_CHIPS } from 'new/components/flows/types/flowTypes';

export const flowDataMap = {
  streaming: {
    title: 'FlexFlow',
    shortTitle: 'FlexFlow',
    subTitle: <>Bi-directional ETL with streaming and real-time data</>,
    category: FLOW_CATEGORY.flow_types,
    chip: FLOW_CHIPS.recommended,
    description: (
      <>
        Create powerful, all-in-one data flows that support both streaming and real-time data. Move
        data from any source to may destinations and apply modifications iteratively, creating rich
        data products along the way.
      </>
    ),
    IconComponent: Streaming,
    ImageComponent: <StreamingDiagram />,
    videoUrl: 'https://vimeo.com/954104369/c93d7b206d',
  },
  spark: {
    title: 'Spark ETL',
    shortTitle: 'Spark ETL',
    subTitle: <>Big data processing with Spark for maximum throughput</>,
    category: FLOW_CATEGORY.flow_types,
    chip: null,
    description: (
      <>
        Modify big data workloads with Spark or EMR in Databricks or cloud data stores. Use when
        minimizing latency is critical, though it only supports one destination at a time.
      </>
    ),
    IconComponent: Spark,
    ImageComponent: <SparkDiagram />,
    videoUrl: 'https://vimeo.com/954106607/e8fd20a508',
  },
  in_memory: {
    title: 'DirectFlow',
    shortTitle: 'DirectFlow',
    subTitle: <>Maximum throughput point-to-point data movement</>,
    category: FLOW_CATEGORY.flow_types,
    chip: FLOW_CHIPS.early_access,
    description: (
      <>
        Perform ETL with maximum throughput when latency is critical. Trade some flexibility
        compared to <em>FlexFlow</em> — no branching or streaming sources - for the fastest
        point-to-point data movement.
      </>
    ),
    IconComponent: InMemory,
    ImageComponent: <InMemoryDiagram />,
    videoUrl: 'https://vimeo.com/1026620410',
  },
  custom: {
    title: 'BYO Flow',
    shortTitle: 'BYO Flow',
    subTitle: <>Bring your own runtime for custom data processing</>,
    category: FLOW_CATEGORY.flow_types,
    chip: FLOW_CHIPS.early_access,
    description: (
      <>
        Incorporate custom runtimes like Python with your own packages, enabling highly specialized
        data processing tailored to specific needs.
      </>
    ),
    IconComponent: BYO,
    ImageComponent: <BYODiagram />,
    videoUrl: 'https://vimeo.com/1026618971',
  },
  automatic: {
    title: 'AutoFlow',
    shortTitle: 'AutoFlow',
    subTitle: (
      <>
        Nexla seamlessly switches between <em>FlexFlow</em> and <em>DirectFlow</em> based on the
        flow definition.
      </>
    ),
    category: FLOW_CATEGORY.flow_types,
    chip: null,
    description: (
      <>
        Use AutoFlow to automatically select between <em>FlexFlow</em> and <em>DirectFlow</em> based
        on your flow definition, ensuring the best balance of performance and flexibility.
      </>
    ),
    IconComponent: Auto,
    ImageComponent: <AutomaticDiagram />,
    videoUrl: 'https://vimeo.com/868958392/51f92e7e3a',
  },
  replication: {
    title: 'Replication',
    shortTitle: 'Replication',
    subTitle: <>High-speed file transfers retaining original structure</>,
    category: FLOW_CATEGORY.flow_types,
    chip: null,
    description: (
      <>
        Perform high-speed file transfers between storage systems when retaining file structure is
        crucial.
      </>
    ),
    IconComponent: Replication,
    ImageComponent: <ReplicationDiagram />,
    videoUrl: 'https://vimeo.com/954105676/d54bf87ea3',
  },
  cdc: {
    title: 'CDC',
    shortTitle: 'CDC',
    subTitle: <>Sync database changes in near real time</>,
    category: FLOW_CATEGORY.flow_types,
    chip: null,
    description: (
      <>Replicate multiple tables across databases and cloud warehouses with just a few clicks.</>
    ),
    IconComponent: CDC,
    ImageComponent: <CDCDiagram />,
    videoUrl: 'https://vimeo.com/954107169/65af21205c',
  },
  elt: {
    title: 'ELT',
    shortTitle: 'ELT',
    subTitle: <>Replicate from APIs and SaaS to Warehouse</>,
    category: FLOW_CATEGORY.flow_types,
    chip: FLOW_CHIPS.early_access,
    description: (
      <>
        Easily load data from APIs directly into your database or warehouse. This streamlined flow
        is designed for a quick setup.
      </>
    ),
    IconComponent: ELT,
    ImageComponent: <ELTDiagram />,
    videoUrl: 'https://vimeo.com/1026620019',
  },
  genai_rag: {
    title: 'RAG',
    shortTitle: 'RAG Flow',
    subTitle: <>Build AI-ready data pipelines for retrieval + generation</>,
    category: FLOW_CATEGORY.flow_types,
    chip: null,
    description: (
      <>
        Power interactive apps like chatbots with your own data. Our RAG flows use GenAI to deliver
        quick, accurate, and easy-to-understand answers to user questions.
      </>
    ),
    IconComponent: RAG,
    ImageComponent: <RAGDiagram />,
    videoUrl: 'https://vimeo.com/1069261133',
  },
  api_server: {
    title: 'API Server',
    shortTitle: 'API Server',
    subTitle: <>Create Data APIs for your applications and databases</>,
    category: FLOW_CATEGORY.flow_types,
    chip: null,
    description: (
      <>
        Create RESTful APIs directly for your applications and databases. Define endpoints,
        parameters, and responses for seamless and reliable integration with external apps.
      </>
    ),
    IconComponent: APIServer,
    ImageComponent: <APIServerIcon />,
    videoUrl: 'https://vimeo.com/1117232514',
  },
} as const;

export const flowDataMapItems = {
  [FLOW_CATEGORY.flow_types]: [
    'streaming',
    'elt',
    'cdc',
    'spark',
    'in_memory',
    'replication',
    'custom',
    'api_server',
    'genai_rag',
    // 'automatic',
  ],
  [FLOW_CATEGORY.flow_wizard]: [
    // 'elt',
    // 'genai_rag'
  ],
  [FLOW_CATEGORY.coming_soon]: [],
};
